import React, { useState, useEffect } from 'react';
import { ShoppingCart, MessageCircle, CheckCircle, Clock, XCircle, Search, Download } from 'lucide-react';
import { SupportTicketModal } from '../SupportTicketModal';
import { OrderDetailsModal } from '../OrderDetailsModal';
import { wsService } from '../../services/WebSocketService';

interface Order {
  orderId: string;
  userId: string;
  email: string;
  name: string;
  county: string;
  state: string;
  amount: number;
  stripePaymentId: string;
  status: 'paid' | 'pending' | 'failed';
  fulfilled: boolean;
  createdAt: string;
  supportTickets?: number;
}

interface TaxLatesDataOrdersProps {
  initialOrderId?: string;
}

export function TaxLatesDataOrders({ initialOrderId }: TaxLatesDataOrdersProps = {}) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'pending' | 'failed'>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  // Subscribe to WebSocket order updates
  useEffect(() => {
    const handleOrderUpdate = (message: any) => {
      console.log('Received order update via WebSocket:', message);
      // Refresh orders list when any order is created or updated
      fetchOrders();
    };

    wsService.on('orderUpdate', handleOrderUpdate);

    return () => {
      wsService.off('orderUpdate', handleOrderUpdate);
    };
  }, []);

  // Handle initial order selection from deep link (only run once on mount)
  useEffect(() => {
    if (initialOrderId && orders.length > 0 && !selectedOrder && !showOrderDetails) {
      const order = orders.find(o => o.orderId === initialOrderId);
      if (order) {
        setSelectedOrder(order);
        setShowOrderDetails(true); // Open details modal instead of chat
      } else {
        console.warn(`Tax list order ${initialOrderId} not found`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrderId, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/taxlatesdata-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsFulfilled = async (orderId: string) => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token');
      }

      await fetch(`${import.meta.env.VITE_API_URL}/admin/taxlatesdata-orders/${orderId}/fulfill`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      fetchOrders();
    } catch (error) {
      console.error('Error fulfilling order:', error);
      alert('Failed to mark order as fulfilled');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.county.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const styles = {
      paid: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
    };

    const icons = {
      paid: <CheckCircle className="w-4 h-4" />,
      pending: <Clock className="w-4 h-4" />,
      failed: <XCircle className="w-4 h-4" />,
    };

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status as keyof typeof styles]}`}>
        {icons[status as keyof typeof icons]}
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-tdb-accent rounded-xl flex items-center justify-center">
            <ShoppingCart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-tdb-primary">TaxLatesData Orders</h2>
            <p className="text-sm text-gray-600">Manage tax delinquent list purchases</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email, name, county, or state..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-tdb-accent focus:outline-none"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-tdb-accent focus:outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Location</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Tickets</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr
                    key={order.orderId}
                    onClick={() => {
                      setSelectedOrder(order);
                      setShowOrderDetails(true);
                    }}
                    className="hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{order.orderId.slice(0, 12)}...</div>
                      <div className="text-xs text-gray-500">{order.stripePaymentId.slice(0, 15)}...</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">{order.name}</div>
                      <div className="text-sm text-gray-500">{order.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.county}, {order.state}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">${order.amount}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{new Date(order.createdAt).toLocaleDateString()}</div>
                      <div className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order);
                          setShowSupportModal(true);
                        }}
                        className="flex items-center space-x-1 px-3 py-1 bg-tdb-accent hover:bg-tdb-accent-dark text-white text-xs font-semibold rounded-lg transition-colors"
                      >
                        <MessageCircle className="w-3 h-3" />
                        <span>Chat ({order.supportTickets || 0})</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {order.fulfilled && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg">
                            Fulfilled
                          </span>
                        )}
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                          <Download className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Orders</div>
          <div className="text-2xl font-bold text-gray-900">{orders.length}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Revenue</div>
          <div className="text-2xl font-bold text-green-600">
            ${orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + o.amount, 0)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Pending</div>
          <div className="text-2xl font-bold text-yellow-600">
            {orders.filter(o => o.status === 'pending').length}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Support Tickets</div>
          <div className="text-2xl font-bold text-tdb-accent">
            {orders.reduce((sum, o) => sum + (o.supportTickets || 0), 0)}
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          isOpen={showOrderDetails}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
          onOpenChat={() => {
            setShowOrderDetails(false);
            setShowSupportModal(true);
          }}
          onRefresh={fetchOrders}
          isAdmin={true}
        />
      )}

      {/* Support Ticket Modal */}
      {selectedOrder && (
        <SupportTicketModal
          isOpen={showSupportModal}
          onClose={() => {
            setShowSupportModal(false);
            setSelectedOrder(null);
          }}
          orderId={selectedOrder.orderId}
          isAdmin={true}
        />
      )}
    </div>
  );
}
